import { Alternative, ServerError, Spinner } from "@lysaab/ui-2";
import React, { useContext, useState } from "react";
import { SignupContext } from "../../../state/SignupContext";
import { PepContext } from "@lysaab/lysa-pep";
import { LocalizationContext } from "../../../state/LocalizationContext";
import { useIntl } from "react-intl";
import {
  EventTracker,
  TrackerEvent,
} from "../../../utils/eventTracker/EventTracker";
import { saveSignupNetsFI, SignupId } from "../../../data/signup";
import {
  AccountType,
  messages,
} from "../../../pages/confirmation/ConfirmationHelpers";
import { createSignupDataNets } from "./ConfirmationHelpers";
import { ConfirmationHeader } from "../../../pages/confirmation/ConfirmationHeader";
import { ConfirmationErrorSnackbar } from "../../../pages/confirmation/ConfirmationErrorSnackbar";
import { ConfirmationTextSection } from "../../../pages/confirmation/ConfirmationTextSection";
import { ConfirmationSummarySection } from "../../../pages/confirmation/ConfirmationSummarySection";
import { ConfirmationNextButton } from "../../../pages/confirmation/ConfirmationNextButton";
import { useDocumentsInformation } from "../../../hooks/useDocumentsInformation";
import { useAdvice } from "../../../state/AdviceContext";
import { Bank, Deposits } from "../../FinlandContext";

interface Props {
  banks?: Alternative<Bank>[];
  deposits?: Deposits;
  next: (signupId: SignupId) => void;
}

export const Confirmation = ({ banks, deposits, next }: Props) => {
  const signupContext = useContext(SignupContext);
  const pepContext = useContext(PepContext);
  const localizationContext = useContext(LocalizationContext);
  const advice = useAdvice();
  const intl = useIntl();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    information,
    error: documentsError,
    isLoading: isLoadingInformation,
  } = useDocumentsInformation();

  const setSignupContextState = signupContext.setState;

  const signup = () => {
    EventTracker.track({ event: TrackerEvent.CONFIRM });

    const signupRequestNets = createSignupDataNets(
      localizationContext.state,
      intl.formatMessage(messages.accountName),
      signupContext.state,
      pepContext.state,
      advice.input,
      advice.result,
      banks,
      deposits
    );

    if (signupRequestNets === null) {
      console.error(
        "signupRequestNets === null",
        JSON.stringify(
          {
            localizationContext: {
              currency: localizationContext.state.currency,
              language: localizationContext.state.language,
              country: localizationContext.state.country,
              // situation is too large to log
            },
            signupContext: signupContext.state,
            pepContext: pepContext.state,
            banks,
            deposits,
          },
          null,
          2
        )
      );

      setError(true);

      return;
    }

    // We expire the cookie in one year + one month. We ask the customers to
    // login at least once a year to fill out the yearly-review, so this should
    // be long enough for them to login again and refresh the cookie
    const maxAge = 60 * 60 * 24 * 395;

    // signup.lysa.se -> lysa.se
    // signup.lysa-test.se -> lysa-test.se
    // localhost -> localhost
    const domain = window.location.hostname.split(".").slice(-2).join(".");
    document.cookie = `lysa_nets_email=${signupContext.state.email}; domain=${domain}; path=/; max-age=${maxAge}`;

    setLoading(true);
    saveSignupNetsFI(signupRequestNets)
      .then((response) => {
        setLoading(false);
        setSignupContextState({ signupId: response.signupId });
        EventTracker.track({ event: TrackerEvent.SUBMITTED });
        next(response.signupId);
      })
      .catch((error: ServerError<any>) => {
        console.error(JSON.stringify(error, Object.getOwnPropertyNames(error)));
        setLoading(false);
        setError(true);
      });
  };

  return (
    <div className="confirmation-page">
      <ConfirmationHeader />

      {loading || isLoadingInformation ? (
        <Spinner />
      ) : (
        <>
          {(error || documentsError) && <ConfirmationErrorSnackbar />}
          <ConfirmationTextSection documentsInformation={information} />
          <ConfirmationSummarySection accountType={AccountType.VP} />
          <ConfirmationNextButton onClick={signup} />
        </>
      )}
    </div>
  );
};
